import {
  MENU_ITEM,
  MENU_ITEM_WITH_CHILDREN,
  SUBMENU_ITEM
} from '@/src/components/sidebar/sidebar.styles'
import { IconWrapper } from '@dolfin/ui/stories/IconWrapper'
import { ChevronDown } from '@untitled-ui/icons-react'
import SubSidebar from '@/src/components/sidebar/subSidebar'
import Link from 'next/link'
import React from 'react'
import { SidebarLink } from '@/src/components/sidebar/constants'
import { TABS } from '@dolfin/business/custom_types'

const getUnreadBadgeLabel = (count?: number) => {
  if (!count || count <= 0) return null
  return count > 99 ? '99+' : String(count)
}

const COLLAPSED_BADGE_CLASSNAME =
  'pointer-events-none absolute -right-2 -top-2 inline-flex min-w-[18px] items-center justify-center rounded-full bg-highlight-600 px-1 py-0.5 text-[10px] font-semibold leading-none text-neutral-0 transition-opacity duration-200 md:group-hover:opacity-0'

const EXPANDED_BADGE_CLASSNAME =
  'pointer-events-none absolute right-4 top-1/2 inline-flex min-w-[18px] -translate-y-1/2 items-center justify-center rounded-full bg-highlight-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-neutral-0 opacity-0 transition-opacity duration-200 md:group-hover:opacity-100'

const SidebarItem = ({
  item,
  isActive,
  isChildrenActive,
  visibleSubSidebar,
  setVisibleSubSidebar,
  closeSidebar,
  full
}: {
  item: SidebarLink
  isActive: (item: Omit<SidebarLink, 'id'> & { id: TABS | number | string }) => boolean
  isChildrenActive: (item: SidebarLink) => boolean
  visibleSubSidebar: string | null
  setVisibleSubSidebar: (id: string | null) => void
  closeSidebar: () => void
  full: boolean
}) => {
  const { id, label, href, Icon, subLinks, unreadCount } = item
  const unreadBadgeLabel = getUnreadBadgeLabel(unreadCount)
  const itemAriaLabel = unreadBadgeLabel
    ? `${label}, ${unreadBadgeLabel} unread`
    : label
  const collapsedBadgeClassName = full ? 'hidden' : COLLAPSED_BADGE_CLASSNAME
  const expandedBadgeClassName = full
    ? 'ml-auto inline-flex min-w-[18px] items-center justify-center rounded-full bg-highlight-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-neutral-0'
    : EXPANDED_BADGE_CLASSNAME

  if (subLinks && subLinks.length > 0) {
    return (
      <li key={id}>
        <div>
          <div
            role='button'
            tabIndex={0}
            aria-label={itemAriaLabel}
            onClick={() => {
              setVisibleSubSidebar(visibleSubSidebar === id ? null : id)
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                setVisibleSubSidebar(visibleSubSidebar === id ? null : id)
              }
            }}
            className={`relative ${
              (isActive(item) || visibleSubSidebar === id) &&
              MENU_ITEM_WITH_CHILDREN.active
            } ${
              isChildrenActive(item) && MENU_ITEM_WITH_CHILDREN.childActive
            } ${
              !isChildrenActive(item) &&
              !(isActive(item) || visibleSubSidebar === id) &&
              MENU_ITEM_WITH_CHILDREN.regular
            }`}>
            <span className='relative shrink-0'>
              <Icon width={18} className={'shrink-0'} />
              {unreadBadgeLabel && (
                <span
                  aria-hidden='true'
                  role='presentation'
                  className={collapsedBadgeClassName}>
                  {unreadBadgeLabel}
                </span>
              )}
            </span>
            <p
              className={
                'md:hidden md:group-hover:block text-xs font-medium whitespace-nowrap overflow-hidden'
              }>
              {label}
            </p>
            {unreadBadgeLabel && (
              <span
                aria-hidden='true'
                role='presentation'
                className={expandedBadgeClassName}>
                {unreadBadgeLabel}
              </span>
            )}
            {full && (
              <div className={'flex grow justify-end'}>
                <IconWrapper
                  width={16}
                  className={`transition-transform focus:outline-0 m-0 p-0 shrink-0 ${
                    (isActive(item) || visibleSubSidebar === id) && 'rotate-180'
                  }`}
                  Icon={ChevronDown}></IconWrapper>
              </div>
            )}
          </div>
        </div>

        <SubSidebar
          onClose={() => setVisibleSubSidebar(null)}
          full={full}
          label={label}
          visible={visibleSubSidebar === id}>
          {subLinks.map(subItem => {
            const { id, label, href, Icon } = subItem

            return (
              <li key={id}>
                <Link
                  shallow
                  onClick={() => closeSidebar()}
                  href={href}
                  className={
                    isActive(subItem)
                      ? SUBMENU_ITEM.active
                      : SUBMENU_ITEM.regular
                  }>
                  <Icon width={18} className={'shrink-0'} />

                  <p
                    className={
                      'block text-xs font-medium whitespace-nowrap overflow-hidden'
                    }>
                    {label}
                  </p>
                </Link>
              </li>
            )
          })}
        </SubSidebar>
      </li>
    )
  }

  return (
    <li key={id}>
      <Link
        shallow
        aria-label={itemAriaLabel}
        onClick={() => {
          closeSidebar()
          setVisibleSubSidebar(null)
        }}
        href={href}
        className={`relative ${
          isActive(item) ? MENU_ITEM.active : MENU_ITEM.regular
        }`}>
        <span className='relative shrink-0'>
          <Icon width={18} className={'shrink-0'} />
          {unreadBadgeLabel && (
            <span
              aria-hidden='true'
              role='presentation'
              className={collapsedBadgeClassName}>
              {unreadBadgeLabel}
            </span>
          )}
        </span>

        <p
          className={
            'md:hidden md:group-hover:block text-xs font-medium whitespace-nowrap overflow-hidden'
          }>
          {label}
        </p>
        {unreadBadgeLabel && (
          <span
            aria-hidden='true'
            role='presentation'
            className={expandedBadgeClassName}>
            {unreadBadgeLabel}
          </span>
        )}
      </Link>
    </li>
  )
}

export default SidebarItem
